/**
 * Tenant roles and the dashboard API's role policy.
 *
 * The table lives here rather than in `apps/dashboard-api` so the dashboard
 * SPA can hide a control using the same rule the API enforces, instead of a
 * copy of it that drifts (task 181). The API applies it in
 * `apps/dashboard-api/src/lib/authorization.ts`, whose tests hold the table to
 * what we intend.
 *
 * It is a subpath of its own, `@headerhawk/core/roles`, because the package
 * root re-exports Tinybird and event code a browser bundle has no business
 * loading.
 */
/** Roles are totally ordered; a higher rank subsumes every lower one. */
export const ROLE_RANK = {
    viewer: 0,
    member: 1,
    admin: 2,
    owner: 3,
};
/** True when `value` names one of the four roles. */
export function isUserRole(value) {
    return typeof value === "string" && Object.hasOwn(ROLE_RANK, value);
}
/** True when `role` is `minimum` or above. */
export function hasMinimumRole(role, minimum) {
    return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
/**
 * The roles that satisfy `minimum`, highest first, for copy: "owners, admins
 * and members". Shared so the dashboard's explanations name the same roles as
 * the API's 403 message.
 */
export function describeRolesAtOrAbove(minimum) {
    const names = Object.keys(ROLE_RANK)
        .filter((role) => hasMinimumRole(role, minimum))
        .sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a])
        .map((role) => `${role}s`);
    if (names.length === 1)
        return names[0];
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
/**
 * Authorization policy for every operation the dashboard API implements, keyed
 * `METHOD /openapi/{path}` — the same key shape as `IMPLEMENTED_OPERATIONS` in
 * `apps/dashboard-api/src/handler.ts`, so the two can be diffed mechanically.
 *
 * Defaults applied here:
 * - Reads (`GET`) are open to every authenticated role.
 * - Changes to tenant-wide configuration or billing require owner/admin.
 * - Content-level changes (sites, alert rules) require member or above.
 * - Self-service edits to one's own profile are open to every role.
 */
export const ROLE_POLICY = {
    // --- Unauthenticated surface -------------------------------------------
    "GET /health": "public",
    "POST /waitlist": "public",
    "POST /contact": "public",
    // Authenticity comes from the Stripe webhook signature, not a session.
    "POST /webhooks/stripe": "public",
    // Authenticity comes from the signed OAuth `state`, not a session.
    "GET /integrations/slack/callback": "public",
    // Provisioning: runs before a tenant (and therefore a role) exists.
    "POST /auth/me": "jwt",
    // --- Tenant configuration ----------------------------------------------
    // Onboarding: names the organization and provisions its Descope org.
    "POST /tenants/setup": "owner",
    "PATCH /tenant": "admin",
    // --- Billing ------------------------------------------------------------
    "POST /checkout/session": "admin",
    // Adds a card to the current subscription, so a trial can change plans.
    "POST /checkout/payment-method": "admin",
    // Post-redirect verification of a session only an admin could have started.
    "POST /checkout/verify": "admin",
    "POST /billing/portal": "admin",
    "GET /billing/subscription": "viewer",
    "POST /subscription/update": "admin",
    "GET /usage": "viewer",
    // --- Internal -----------------------------------------------------------
    "POST /internal/test-error": "admin",
    // --- Integrations -------------------------------------------------------
    // A GET, but it starts the OAuth connect flow, so it is gated as a mutation.
    "GET /integrations/slack/authorize": "admin",
    "GET /integrations/slack/channels": "viewer",
    "GET /integrations/slack": "viewer",
    "DELETE /integrations/slack": "admin",
    // --- Feedback -----------------------------------------------------------
    // Deliberately open: anyone using the product can report a problem.
    "POST /feedback": "viewer",
    // --- Reports explorer ---------------------------------------------------
    "GET /reports": "viewer",
    "GET /reports/histogram": "viewer",
    "GET /reports/facets": "viewer",
    "GET /reports/count": "viewer",
    "GET /reports/grouped": "viewer",
    "GET /reports/{reportId}": "viewer",
    // --- Team management ----------------------------------------------------
    "GET /users": "viewer",
    "POST /users/invite": "admin",
    "PATCH /users/{userId}": "admin",
    "DELETE /users/{userId}": "admin",
    // --- Own profile (self-service, open to every role) ---------------------
    "GET /me": "viewer",
    "PATCH /me": "viewer",
    "POST /me/email": "viewer",
    // --- Script analytics ---------------------------------------------------
    "GET /scripts/stats": "viewer",
    "GET /scripts/origins": "viewer",
    "GET /scripts/inventory": "viewer",
    "GET /scripts/pages": "viewer",
    // --- Issues -------------------------------------------------------------
    "GET /issues": "viewer",
    "GET /sites/{siteId}/issues": "viewer",
    "GET /sites/{siteId}/issues/{signature}/reports": "viewer",
    "GET /sites/{siteId}/noise-stats": "viewer",
    // --- Sites --------------------------------------------------------------
    // Member and above, matching what the product already tells people: the
    // invite dialog describes the member role as "Members can view and manage
    // sites" (`apps/web/src/components/settings/invite-user-dialog.tsx`).
    // Tightening these to owner/admin would be a product change, not a security
    // fix — BE-H2 is about `viewer`, who could delete a site and rotate its
    // ingest code, breaking live ingestion for the whole tenant.
    "GET /sites": "viewer",
    "POST /sites": "member",
    "GET /sites/{siteId}": "viewer",
    "PATCH /sites/{siteId}": "member",
    "DELETE /sites/{siteId}": "member",
    "POST /sites/{siteId}/rotate-key": "member",
    "GET /sites/{siteId}/setup": "viewer",
    // A read of the site's own diagnostics. It discloses hosts that reported to
    // this site, which any member of the tenant can already see on the setup
    // page, so it is gated the same way as the rest of the site's reads.
    "GET /sites/{siteId}/dropped-reports": "viewer",
    // Monitored pages (task 306): marking which pages matter is day-to-day site
    // work, like editing the site's hosts. The plan gate is applied in the
    // routes, since it is about the tenant, not the caller's role.
    "GET /sites/{siteId}/pages": "viewer",
    "POST /sites/{siteId}/pages": "member",
    "PATCH /sites/{siteId}/pages/{pageId}": "member",
    "DELETE /sites/{siteId}/pages/{pageId}": "member",
    "GET /sites/{siteId}/page-suggestions": "viewer",
    "GET /sites/{siteId}/csp-policies": "viewer",
    "GET /sites/{siteId}/pages/{pageId}/scans": "viewer",
    "GET /sites/{siteId}/pages/{pageId}/scans/{scanId}": "viewer",
    // Script authorization (task 307): reading the inventory and its evidence
    // is open to every role; deciding is member, like alert rules (Michael,
    // 2026-09-16). Every decision records who made it, which is what an
    // assessor checks.
    "GET /sites/{siteId}/scripts": "viewer",
    "GET /sites/{siteId}/scripts/inventory.csv": "viewer",
    "GET /sites/{siteId}/scripts/{scriptId}": "viewer",
    "POST /sites/{siteId}/scripts/{scriptId}/decision": "member",
    // --- Alerting (task 304, ADR 010) ----------------------------------------
    // A destination decides where tenant data leaves HeaderHawk, the same weight
    // as connecting Slack, so its writes are admin. Choosing which existing
    // destinations hear about what is day-to-day work, so rules are member.
    "GET /alert-destinations": "viewer",
    "POST /alert-destinations": "admin",
    "PATCH /alert-destinations/{destinationId}": "admin",
    "DELETE /alert-destinations/{destinationId}": "admin",
    // Sends to a destination an admin already configured; it cannot change
    // where anything goes, so a member may check it works.
    "POST /alert-destinations/{destinationId}/test": "member",
    "GET /alert-rules": "viewer",
    "POST /alert-rules": "member",
    "PATCH /alert-rules/{ruleId}": "member",
    "DELETE /alert-rules/{ruleId}": "member",
    "GET /alert-event-types": "viewer",
    "GET /alert-deliveries": "viewer",
    "GET /sites/{siteId}/alert-coverage": "viewer",
    // Authenticity comes from the emailed token: the person clicking may have
    // no HeaderHawk account at all.
    "POST /alert-email-confirmations": "public",
};
/**
 * Whether `role` may call `operation`.
 *
 * `public` and `jwt` operations need less than any tenant role, so every role
 * may call them.
 */
export function canPerform(role, operation) {
    const policy = ROLE_POLICY[operation];
    if (policy === "public" || policy === "jwt")
        return true;
    return hasMinimumRole(role, policy);
}
/**
 * The roles that may call `operation`, for copy — "owners and admins" — so an
 * explanation names the same roles as the API's 403 for it.
 */
export function describeRolesAllowedTo(operation) {
    const policy = ROLE_POLICY[operation];
    if (policy === "public" || policy === "jwt")
        return "everyone";
    return describeRolesAtOrAbove(policy);
}
//# sourceMappingURL=index.js.map